import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import i18n from '@/i18n';
import type { AppLanguage } from '@/locales';

export type LanguageCode = AppLanguage;

type I18nContextValue = {
  language: LanguageCode;
  setLanguage: (next: LanguageCode) => void;
  t: (key: string, fallback?: string) => string;
};

const normalizeLanguage = (value?: string | null): LanguageCode => {
  const next = String(value || '').trim().toLowerCase();
  if (next.startsWith('fr')) return 'fr';
  if (next.startsWith('ar')) return 'ar';
  return 'en';
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() =>
    normalizeLanguage(i18n.resolvedLanguage || i18n.language),
  );

  useEffect(() => {
    const handleChange = (next: string) => {
      setLanguageState(normalizeLanguage(next));
    };

    i18n.on('languageChanged', handleChange);
    return () => {
      i18n.off('languageChanged', handleChange);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage: (next) => {
        void i18n.changeLanguage(next);
      },
      t: (key, fallback) => {
        const translated = i18n.t(key, { defaultValue: fallback || key });
        return typeof translated === 'string' ? translated : fallback || key;
      },
    }),
    [language],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
